import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { clientDatabasesAPI, domainsAPI } from '@hpanel/web-common/api/client'
import type {
  ClientDatabase,
  ClientDatabaseDeleteResponse,
  Domain,
} from '@hpanel/web-common/api/types'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import { DatabaseList } from '../../components/Databases/DatabaseList'
import { ManageUsersModal } from '../../components/Databases/ManageUsersModal'
import { RotatePasswordModal } from '../../components/Databases/RotatePasswordModal'
import { DeleteDatabaseDialog } from '../../components/Databases/DeleteDatabaseDialog'
import { ServiceUnavailableBanner } from '../../components/Databases/ServiceUnavailableBanner'
import { ConnectionDetailsModal } from '../../components/Databases/ConnectionDetailsModal'

/**
 * DatabaseManagement page — the W2-D rewrite.
 *
 * Composition:
 *   - Top-level state owns the data (databases, domains, loading,
 *     serviceDown) and which modal is open.
 *   - Loose-coupled child components own their own form state and
 *     receive callbacks (onCreated, onDeleted, onChanged) so the page
 *     can refetch.
 *   - 503 from `/api/client/databases` (list) flips serviceDown=true
 *     and the page renders the §9 banner instead of an empty list +
 *     toast. The "Create" button is hidden in that mode — there's
 *     nothing the user can do until the operator brings the service
 *     back.
 */

export const DatabaseManagement: React.FC = () => {
  const { user } = useAuth()
  const [databases, setDatabases] = useState<ClientDatabase[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [serviceDown, setServiceDown] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  // Modal flags / targets — only one is open at a time.
  const [manageDb, setManageDb] = useState<ClientDatabase | null>(null)
  const [rotateDb, setRotateDb] = useState<ClientDatabase | null>(null)
  const [deleteDb, setDeleteDb] = useState<ClientDatabase | null>(null)
  const [connectionDb, setConnectionDb] = useState<ClientDatabase | null>(null)

  const fetchDatabases = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await clientDatabasesAPI.list()
      setDatabases(res.databases)
      setServiceDown(false)
    } catch (e) {
      const apiErr = e as { status?: number; message?: string }
      if (apiErr.status === 503) {
        // §9: clean banner, never the raw 503 message.
        setServiceDown(true)
        setDatabases([])
      } else {
        // eslint-disable-next-line no-console
        console.error('[DatabaseManagement] list failed', e)
        setError(
          e instanceof Error ? e.message : 'Failed to load databases',
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchDomains = async () => {
    try {
      const res = await domainsAPI.list(user?.id)
      setDomains(res.domains)
    } catch (e) {
      // Domains are only needed for the create form; don't block the page.
      // eslint-disable-next-line no-console
      console.error('[DatabaseManagement] domains list failed', e)
    }
  }

  useEffect(() => {
    fetchDatabases()
    fetchDomains()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-clear the toast after a moment so it doesn't pile up.
  useEffect(() => {
    if (!toast) return
    const id = window.setTimeout(() => setToast(null), 6000)
    return () => window.clearTimeout(id)
  }, [toast])

  const onDeleted = (result: ClientDatabaseDeleteResponse) => {
    const droppedClause =
      result.dropped_users.length > 0
        ? ` (dropped users: ${result.dropped_users.join(', ')})`
        : ''
    setToast(`Deleted ${result.deleted_db_name}${droppedClause}.`)
    fetchDatabases()
  }

  return (
    <>
      <div data-testid="database-management-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1
              data-testid="page-title"
              className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
            >
              Database Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage your databases, users, and permissions.
            </p>
          </div>

          {!serviceDown && (
            <Link
              to="/client/databases/new"
              data-testid="create-database-button"
              className="btn-primary self-start sm:self-auto"
            >
              + Create Database
            </Link>
          )}
        </div>

        {/* §9 banner */}
        {serviceDown && <ServiceUnavailableBanner />}

        {/* Generic error (non-503) */}
        {error && !serviceDown && (
          <div
            data-testid="db-page-error"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {/* Toast for successes */}
        {toast && (
          <div
            data-testid="db-toast"
            role="status"
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-800 dark:text-green-200"
          >
            {toast}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading databases…
          </div>
        )}

        {/* Empty state (only when service is up) */}
        {!loading && !serviceDown && !error && databases.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No databases yet. Create your first database to get started.
            </p>
          </div>
        )}

        {/* List */}
        {!loading && !serviceDown && databases.length > 0 && (
          <DatabaseList
            databases={databases}
            domains={domains}
            onManageUsers={setManageDb}
            onRotatePrimary={setRotateDb}
            onDelete={setDeleteDb}
            onShowConnection={setConnectionDb}
          />
        )}
      </div>

      {manageDb && (
        <ManageUsersModal
          database={manageDb}
          onClose={() => setManageDb(null)}
        />
      )}

      {rotateDb && (
        <RotatePasswordModal
          target={{ kind: 'primary', dbId: rotateDb.id }}
          username={rotateDb.username ?? rotateDb.name}
          onClose={() => setRotateDb(null)}
          onRotated={() => setToast(`Rotated password for ${rotateDb.name}.`)}
        />
      )}

      {deleteDb && (
        <DeleteDatabaseDialog
          database={deleteDb}
          onClose={() => setDeleteDb(null)}
          onDeleted={onDeleted}
        />
      )}

      {connectionDb && (
        <ConnectionDetailsModal
          db={connectionDb}
          onClose={() => setConnectionDb(null)}
        />
      )}
    </>
  )
}
